import { useState, useEffect } from "react";

const STORAGE_KEY = "ios-install-prompt-dismissed";
const DISMISS_DURATION_DAYS = 7; // Show again after 7 days

/**
 * Detects if the user is on iOS Safari and not in standalone mode (PWA installed)
 */
function useIOSInstallPrompt() {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    // Check if already in standalone mode (installed as PWA)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;

    // Check if running in Safari (not Chrome, Firefox, etc. on iOS)
    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      !/CriOS|FxiOS/.test(navigator.userAgent);

    // Check if user dismissed it recently
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    const isDismissedRecently =
      dismissedAt &&
      Date.now() - parseInt(dismissedAt) <
        DISMISS_DURATION_DAYS * 24 * 60 * 60 * 1000;

    setShouldShow(isIOS && !isStandalone && isSafari && !isDismissedRecently);
  }, []);

  const dismiss = (permanent: boolean = false) => {
    if (permanent) {
      // Set a far future timestamp to effectively never show again
      localStorage.setItem(
        STORAGE_KEY,
        String(Date.now() + 365 * 24 * 60 * 60 * 1000 * 10)
      );
    } else {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
    setShouldShow(false);
  };

  return { shouldShow, dismiss };
}

export function IOSInstallPrompt() {
  const { shouldShow, dismiss } = useIOSInstallPrompt();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!shouldShow) return null;

  return (
    <>
      {/* Collapsed Banner */}
      {!isExpanded && (
        <div
          className="fixed bottom-20 left-4 right-4 z-40 animate-in fade-in slide-in-from-bottom-4 duration-300"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div
            onClick={() => setIsExpanded(true)}
            className="mx-auto max-w-md rounded-2xl border border-primary/30 bg-card-light/95 dark:bg-card-dark/95 backdrop-blur-lg p-3 shadow-lg cursor-pointer hover:border-primary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {/* App Icon */}
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-primary text-xl">
                  install_mobile
                </span>
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-content-light dark:text-white text-sm font-semibold truncate">
                  Install Dasher App
                </p>
                <p className="text-slate-500 dark:text-slate-400 text-xs truncate">
                  Tap for quick install guide
                </p>
              </div>

              {/* Arrow */}
              <span className="material-symbols-outlined text-slate-400 text-xl shrink-0">
                chevron_right
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Modal */}
      {isExpanded && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => setIsExpanded(false)}
          />

          {/* Modal Content */}
          <div
            className="relative w-full max-w-md bg-card-light dark:bg-card-dark rounded-t-3xl p-6 animate-in slide-in-from-bottom duration-300"
            style={{
              paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))",
            }}
          >
            {/* Handle bar */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-10 h-1 bg-slate-600 rounded-full" />

            {/* Close button */}
            <button
              onClick={() => setIsExpanded(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-700/50 flex items-center justify-center hover:bg-slate-600/50 transition-colors"
            >
              <span className="material-symbols-outlined text-slate-300 text-lg">
                close
              </span>
            </button>

            {/* Header */}
            <div className="text-center mb-6 mt-2">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-3">
                <img
                  src="/icon-192.png"
                  alt="Dasher"
                  className="w-12 h-12 rounded-xl"
                  onError={(e) => {
                    // Fallback to icon if image fails
                    (e.target as HTMLImageElement).style.display = "none";
                    (
                      e.target as HTMLImageElement
                    ).nextElementSibling?.classList.remove("hidden");
                  }}
                />
              </div>
              <h2 className="text-content-light dark:text-white text-xl font-bold">Install Dasher</h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                Add to your home screen for the best experience
              </p>
            </div>

            {/* Steps */}
            <div className="space-y-3 mb-6">
              {/* Step 1 */}
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <span className="text-primary font-bold text-xs">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-content-light dark:text-white text-sm font-medium">
                    Tap the Share button
                  </p>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                    In the Safari toolbar at the bottom(this only works in
                    Safari)
                  </p>
                </div>
                {/* Safari Share Icon */}
                <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center shrink-0">
                  <svg
                    className="w-4 h-4 text-blue-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                </div>
              </div>

              {/* Alternative for new iOS - Three dots menu */}
              <div className="flex items-center gap-2 pl-10">
                <div className="flex-1 border-t border-dashed border-slate-600" />
                <span className="text-slate-500 text-xs px-2">or</span>
                <div className="flex-1 border-t border-dashed border-slate-600" />
              </div>

              <div className="flex items-start gap-3 pl-10">
                <div className="flex-1">
                  <p className="text-slate-300 text-xs">
                    On newer iOS: Tap{" "}
                    <span className="text-white font-medium">&#8943;</span>{" "}
                    (three dots) at the bottom, then tap Share
                  </p>
                </div>
                {/* Three dots icon */}
                <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center shrink-0">
                  <span className="text-blue-400 text-lg font-bold tracking-wider">
                    &#8943;
                  </span>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-3 pt-1">
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <span className="text-primary font-bold text-xs">2</span>
                </div>
                <div className="flex-1">
                  <p className="text-content-light dark:text-white text-sm font-medium">
                    Tap "Add to Home Screen"
                  </p>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                    Scroll down in the share menu to find it
                  </p>
                </div>
                {/* Add to Home Screen Icon */}
                <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-slate-300 text-lg">
                    add_box
                  </span>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <span className="text-primary font-bold text-xs">3</span>
                </div>
                <div className="flex-1">
                  <p className="text-content-light dark:text-white text-sm font-medium">
                    Tap "Add" to confirm
                  </p>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                    Dasher will appear on your home screen
                  </p>
                </div>
                {/* Checkmark Icon */}
                <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-primary text-lg">
                    check_circle
                  </span>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="bg-slate-800/50 rounded-xl p-3 mb-6">
              <p className="text-slate-300 text-xs text-center">
                <span className="text-primary">Why install?</span> Full-screen
                experience, faster loading, and works offline
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  dismiss(true);
                }}
                className="flex-1 rounded-xl bg-slate-700/50 py-3 px-4 text-sm font-medium text-slate-300 hover:bg-slate-600/50 transition-colors"
              >
                Don't show again
              </button>
              <button
                onClick={() => {
                  dismiss(false);
                }}
                className="flex-1 rounded-xl bg-primary py-3 px-4 text-sm font-semibold text-black hover:opacity-90 transition-opacity"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
